/**
 * Auth-relevant route lists — single source of truth.
 *
 * The Edge proxy (`proxy.ts`) and the runtime `AuthProvider` both need
 * to know which paths are auth-only (the login + register screens). One
 * declaration here, two imports — never let these drift.
 */

export const AUTH_ONLY_PATHS = new Set(["/login", "/register"]);

/**
 * Routes that render with NO session and NO backend at all.
 *
 * Only the design gallery, and only in development. It exists to render every
 * primitive against the live palette so the design system can be reviewed
 * without logging in — which matters because the previous way to judge a
 * hover state was to run the app, authenticate, and go hunting for a screen
 * that used the component you changed. That is how a shadow ramp and three
 * gradient tokens shipped with zero consumers.
 *
 * Both the proxy AND the AuthProvider consult this. The proxy alone is not
 * enough: it decides whether the REQUEST is allowed through, while
 * AuthProvider decides whether the TREE renders, and it replaces children
 * with a "can't reach the server" screen when the backend is down — which is
 * exactly the situation the gallery is designed to work in.
 */
const NO_AUTH_PREFIXES = ["/design"] as const;

export function isNoAuthPath(pathname: string): boolean {
  if (process.env.NODE_ENV === "production") return false;
  return NO_AUTH_PREFIXES.some(
    (p) => pathname === p || pathname.startsWith(`${p}/`),
  );
}
